import { http } from '@/api/http';
import { ApiRequestError } from '@/api/client';
import { envelopeMessage } from '@/api/envelope';

const WALLET_INFO_PATH = '/system/team/get_wallet_info';
const CREATE_SALARY_PATH = '/system/salary/create_salary_application';

function readString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

export interface WalletChangeRecord {
  id: string;
  change: number;
  balance: number;
  lastBalance: number;
  changeType: number;
  changeTypeText: string;
  description?: string;
  createDate?: string;
  orderId?: string;
  serviceDuration?: number;
  serviceType?: number;
  remainingDuration?: number;
  changeDuration?: number;
}

/** `get_wallet_info` 返回的 `bank_date` */
export interface WalletBankInfo {
  bankAccountName?: string;
  bankCardNumber?: string;
  bankName?: string;
}

export interface ServicerBankInfo extends WalletBankInfo {
  bankBsb?: string;
  bankAccount?: string;
}

export interface WalletInfoResult {
  balance: number;
  totalRecords: number;
  page: number;
  pageSize: number;
  changeRecords: WalletChangeRecord[];
  bankInfo?: WalletBankInfo;
}

export interface FetchWalletInfoParams {
  page?: number;
  pageSize?: number;
}

export interface CreateSalaryApplicationBody {
  amount: number;
  workStartDate: string;
  workEndDate: string;
  workHours?: number;
  hourlyRate?: number;
  applicationType?: number;
  submitRemark?: string;
}

export interface CreateSalaryApplicationResult {
  error: boolean;
  message: string;
  applicationId?: string;
  documentNumber?: string;
}

function normalizeChangeTypeText(changeType: number, raw?: string): string {
  if (changeType === 1) return 'Income';
  if (changeType === 2) return 'Withdrawal';
  if (raw === '收入') return 'Income';
  if (raw === '提现') return 'Withdrawal';
  if (raw === '其他') return 'Other';
  return raw?.trim() || 'Other';
}

function normalizeWalletBankInfo(raw: unknown): WalletBankInfo | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const bankInfo: WalletBankInfo = {
    bankAccountName: readString(o, ['bank_account_name', 'bankAccountName']),
    bankCardNumber: readString(o, ['bank_card_number', 'bankCardNumber']),
    bankName: readString(o, ['bank_name', 'bankName']),
  };
  if (!bankInfo.bankAccountName && !bankInfo.bankCardNumber && !bankInfo.bankName) {
    return undefined;
  }
  return bankInfo;
}

function normalizeServicerBankInfo(raw: unknown): ServicerBankInfo | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  return {
    bankAccountName: readString(o, ['bank_account_name', 'bankAccountName']),
    bankCardNumber: readString(o, ['bank_card_number', 'bankCardNumber']),
    bankName: readString(o, ['bank_name', 'bankName']),
    bankBsb: readString(o, ['bank_bsb', 'bankBsb']),
    bankAccount: readString(o, ['bank_account', 'bankAccount']),
  };
}

function normalizeWalletRecord(row: unknown, index: number): WalletChangeRecord | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const changeType = readNum(o, ['change_type', 'changeType']) ?? 0;
  const change = readNum(o, ['change']) ?? 0;
  return {
    id: readString(o, ['id']) ?? `wallet-record-${index}`,
    change,
    balance: readNum(o, ['balance']) ?? 0,
    lastBalance: readNum(o, ['last_balance', 'lastBalance']) ?? 0,
    changeType,
    changeTypeText: normalizeChangeTypeText(
      changeType,
      readString(o, ['change_type_text', 'changeTypeText']),
    ),
    description: readString(o, ['description']),
    createDate: readString(o, ['create_date', 'createDate']),
    orderId: readString(o, ['order_id', 'orderId']),
    serviceDuration: readNum(o, ['service_duration', 'serviceDuration']),
    serviceType: readNum(o, ['service_type', 'serviceType']),
    remainingDuration: readNum(o, ['remaining_duration', 'remainingDuration']),
    changeDuration: readNum(o, ['change_duration', 'changeDuration']),
  };
}

function parseWalletInfoPayload(data: unknown): WalletInfoResult {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const payload =
    root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;

  const rows = payload.change_records ?? payload.changeRecords ?? payload.records ?? payload.list;
  const changeRecords = Array.isArray(rows)
    ? rows
        .map((row, index) => normalizeWalletRecord(row, index))
        .filter((item): item is WalletChangeRecord => item !== null)
    : [];

  return {
    balance: readNum(payload, ['balance', 'current_balance', 'currentBalance']) ?? 0,
    totalRecords: readNum(payload, ['total_records', 'totalRecords', 'total']) ?? changeRecords.length,
    page: readNum(payload, ['page']) ?? 1,
    pageSize: readNum(payload, ['page_size', 'pageSize']) ?? changeRecords.length,
    changeRecords,
    bankInfo: normalizeWalletBankInfo(payload.bank_date ?? payload.bankDate),
  };
}

/** POST `/system/team/get_wallet_info` — 获取钱包余额与变动记录 */
export async function fetchWalletInfo(
  params: FetchWalletInfoParams = {},
): Promise<WalletInfoResult> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  const raw = await http.post<Record<string, unknown>>(
    WALLET_INFO_PATH,
    { page, page_size: pageSize },
    { skipApiEnvelope: true },
  );

  if (raw.error === true) {
    throw new ApiRequestError(envelopeMessage(raw, 'Failed to load wallet info.'));
  }

  return parseWalletInfoPayload(raw);
}

const ADMIN_USER_WALLET_PATH = '/system/team/admin_get_user_wallet_info';

export interface FetchAdminUserWalletInfoParams {
  userId: string | number;
  page?: number;
  pageSize?: number;
}

/** POST `/system/team/admin_get_user_wallet_info` — 管理员获取指定用户钱包余额与变动记录 */
export async function fetchAdminUserWalletInfo(
  params: FetchAdminUserWalletInfoParams,
): Promise<WalletInfoResult> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  const formData = new FormData();
  formData.append('user_id', String(params.userId));
  formData.append('page', String(page));
  formData.append('page_size', String(pageSize));

  const raw = await http.post<Record<string, unknown>>(
    ADMIN_USER_WALLET_PATH,
    formData,
    { skipApiEnvelope: true },
  );

  if (raw.error === true) {
    throw new ApiRequestError(envelopeMessage(raw, 'Failed to load member wallet info.'));
  }

  return parseWalletInfoPayload(raw);
}

/** POST `/system/team/op/apply_withdrawal` — 服务人员申请提现 */
export interface ApplyWithdrawalBody {
  amount: number;
  bankName: string;
  bankAccountName: string;
  bankCardNumber: string;
  /** 可选，指定审批团队ID。多团队成员可选择由哪个团队的管理员审批；不传则使用当前活跃团队 */
  teamId?: number;
}

export interface ApplyWithdrawalResult {
  error: boolean;
  message: string;
  withdrawalId?: string;
  remainingBalance?: number;
}

export async function postApplyWithdrawal(body: ApplyWithdrawalBody): Promise<ApplyWithdrawalResult> {
  const failFallback = 'Failed to submit withdrawal application.';
  const okFallback = 'Withdrawal application submitted successfully.';

  try {
    const formData = new FormData();
    formData.append('amount', String(body.amount));
    formData.append('bank_name', body.bankName.trim());
    formData.append('bank_account_name', body.bankAccountName.trim());
    formData.append('bank_card_number', normalizeBankCardDigits(body.bankCardNumber));
    if (typeof body.teamId === 'number' && body.teamId > 0) {
      formData.append('team_id', String(body.teamId));
    }

    const res = await http.post<Record<string, unknown>>(
      '/system/team/op/apply_withdrawal',
      formData,
      { skipApiEnvelope: true },
    );

    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }

    const data = res.data && typeof res.data === 'object' ? (res.data as Record<string, unknown>) : {};
    return {
      error: false,
      message: envelopeMessage(res, okFallback),
      withdrawalId: readString(data, ['withdrawal_id', 'withdrawalId']),
      remainingBalance: readNum(data, ['remaining_balance', 'remainingBalance']),
    };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** POST `/system/salary/create_salary_application` — 申请工资（全额提现） */
export async function postCreateSalaryApplication(
  body: CreateSalaryApplicationBody,
): Promise<CreateSalaryApplicationResult> {
  const failFallback = 'Failed to submit salary application.';
  const okFallback = 'Salary application submitted successfully.';

  try {
    const res = await http.post<Record<string, unknown>>(
      CREATE_SALARY_PATH,
      {
        amount: body.amount,
        work_start_date: body.workStartDate,
        work_end_date: body.workEndDate,
        application_type: body.applicationType ?? 1,
        work_hours: body.workHours,
        hourly_rate: body.hourlyRate,
        submit_remark: body.submitRemark?.trim() || undefined,
      },
      { skipApiEnvelope: true },
    );

    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }

    const data = res.data && typeof res.data === 'object' ? (res.data as Record<string, unknown>) : {};
    return {
      error: false,
      message: envelopeMessage(res, okFallback),
      applicationId: readString(data, ['application_id', 'applicationId']),
      documentNumber: readString(data, ['document_number', 'documentNumber']),
    };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

export function formatWalletAmount(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

/** 是否已设置银行卡信息（以卡号为准） */
export function hasWalletBankInfo(info?: WalletBankInfo | null): boolean {
  return Boolean(info?.bankCardNumber?.trim());
}

/** 脱敏显示银行卡号，仅保留后 4 位 */
export function maskBankCardNumber(cardNumber: string): string {
  const digits = normalizeBankCardDigits(cardNumber);
  if (!digits) return '—';
  if (digits.length <= 4) return `**** ${digits}`;
  return `**** **** **** ${digits.slice(-4)}`;
}

const BANK_CARD_MIN_LENGTH = 13;
const BANK_CARD_MAX_LENGTH = 19;

/** 提取卡号中的数字 */
export function normalizeBankCardDigits(cardNumber: string): string {
  return cardNumber.replace(/\D/g, '');
}

/** 输入时格式化为 4 位一组，最多 19 位数字 */
export function formatBankCardInput(cardNumber: string): string {
  const digits = normalizeBankCardDigits(cardNumber).slice(0, BANK_CARD_MAX_LENGTH);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function passesLuhnCheck(digits: string): boolean {
  let sum = 0;
  let shouldDouble = false;

  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = Number(digits[i]);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
}

/** 校验银行卡号格式，合法返回 null，否则返回错误文案 */
export function validateBankCardNumber(cardNumber: string): string | null {
  const digits = normalizeBankCardDigits(cardNumber);

  if (!digits) {
    return 'Please enter card number.';
  }
  if (!/^\d+$/.test(digits)) {
    return 'Card number must contain digits only.';
  }
  if (digits.length < BANK_CARD_MIN_LENGTH || digits.length > BANK_CARD_MAX_LENGTH) {
    return `Card number must be ${BANK_CARD_MIN_LENGTH}-${BANK_CARD_MAX_LENGTH} digits.`;
  }
  if (!passesLuhnCheck(digits)) {
    return 'Please enter a valid card number.';
  }

  return null;
}

const GET_BANK_INFO_PATH = '/system/servicer/get_bank_info';
const EDIT_BANK_INFO_PATH = '/system/team/op/update_my_bank_info';

/** POST `/system/servicer/get_bank_info` — 获取服务人员银行卡信息 */
export async function fetchServicerBankInfo(): Promise<ServicerBankInfo | undefined> {
  const raw = await http.post<Record<string, unknown>>(
    GET_BANK_INFO_PATH,
    {},
    { skipApiEnvelope: true },
  );

  if (raw.error === true) {
    throw new ApiRequestError(envelopeMessage(raw, 'Failed to load bank information.'));
  }

  const data = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  return normalizeServicerBankInfo(data);
}

export interface EditBankInfoBody {
  bankAccountName: string;
  bankCardNumber?: string;
  bankName?: string;
}

/** POST `/system/team/op/update_my_bank_info` — 编辑服务人员银行卡信息 */
export async function postEditBankInfo(body: EditBankInfoBody): Promise<void> {
  const formData = new FormData();
  formData.append('bank_account_name', body.bankAccountName.trim());
  formData.append('bank_bsb', '');
  formData.append('bank_account', '');
  formData.append('bank_card_number', body.bankCardNumber?.trim() ?? '');
  formData.append('bank_name', body.bankName?.trim() ?? '');

  const raw = await http.post<Record<string, unknown>>(
    EDIT_BANK_INFO_PATH,
    formData,
    { skipApiEnvelope: true },
  );

  if (raw.error === true) {
    throw new ApiRequestError(envelopeMessage(raw, 'Failed to save bank information.'));
  }
}
